import { createSelector } from 'reselect';

import { Passenger } from '../../../Passengers/types';
import { SearchFormState } from '../reducers';
import { PassengerType } from '../../../enums';
import { getPassengersConfig } from '../passengersConfig/selectors';
import { AviaPassengerType, PassengerTypeSearchParameter } from '@websky/graphql';

export const getPassengersState = (state: SearchFormState) => state.passengers;

/**
 * Create an array of passengers info for displaying in the dropdown menu.
 */
export const getPassengers = createSelector(getPassengersState, passengers => {
	const result: Passenger[] = [];

	for (const type in passengers) {
		if (passengers.hasOwnProperty(type)) {
			result.push({
				type: type as PassengerType,
				count: passengers[type] || 0
			});
		}
	}

	return result;
});

export const getPassengersForRequest = createSelector(getPassengers, getPassengersConfig, (passengers, config) => {
	const passengersForRequest: PassengerTypeSearchParameter[] = [];

	for (const passenger of passengers) {
		const passengerConfig = config[passenger.type];

		if (!config[passenger.type]) {
			continue;
		}

		passengersForRequest.push({
			passengerType: passengerConfig?.parentCode
				? (passengerConfig.parentCode as AviaPassengerType)
				: (passenger.type as any),
			extendedPassengerType: passengerConfig?.parentCode ? passenger.type : null,
			count: passenger.count
		});
	}

	return passengersForRequest;
});

/**
 * Count a total number of the selected passengers.
 */
export const getTotalPassengersCount = createSelector(getPassengers, (passengers): number =>
	passengers.reduce((total, passenger) => total + passenger.count, 0)
);

export const getPassengersErrors = createSelector(
	getPassengers,
	getPassengersConfig,
	getTotalPassengersCount,
	(state, passengersConfig, totalPassCount) => {
		// no empty
		if (totalPassCount === 0) {
			return { error: 'Please select passengers' };
		}

		const passengersWithRequiredOneOf = Object.values(passengersConfig).filter(
			passengerConfig => passengerConfig.requiredWithOneOf?.length
		);

		// all required selected
		const selectedPassengers = state.filter(statePassenger => statePassenger.count);
		const selectedPassengersTypes = selectedPassengers.map(({ type }) => type);

		for (const requiredOneOfPass of passengersWithRequiredOneOf) {
			// passenger with required selected
			if (selectedPassengersTypes.includes(requiredOneOfPass.code as PassengerType)) {
				const requiredPassengers = requiredOneOfPass.requiredWithOneOf;

				const isSomeOfRequiredSelected = requiredPassengers.some(requiredPassengerCode =>
					selectedPassengersTypes.includes(requiredPassengerCode as PassengerType)
				);

				if (!isSomeOfRequiredSelected) {
					return { error: `required passengers`, interpolateData: requiredPassengers };
				}
			}
		}

		return undefined;
	}
);

export const getHasPassengersErrors = createSelector(getPassengersErrors, errors => errors !== undefined);
